import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Input, Pagination, Spin } from 'antd'
import { BiSearch } from 'react-icons/bi'
import { changeHeader } from '../../../redux/reducer/headerSlice'
import { useDispatch } from 'react-redux'
import handleGet from '../../../utils/Get'
import { BASE_URL } from '../../../utils/config'
import { useTranslation } from 'react-i18next'
import { FaEye } from 'react-icons/fa'

const UserManagement = () => {
    const dispatch = useDispatch()
    const [searchUser, setSearchUser] = useState('')
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [current, setCurrent] = useState(1)
    const itemsPerPage = 8
    const { t } = useTranslation()
    const { UsersManagement } = t('AgencyManagement', { returnObjects: true })

    const startIndex = (current - 1) * itemsPerPage
    const endIndex = current * itemsPerPage
    const itemsToDisplay = data?.slice(startIndex, endIndex)

    const onChange = (page) => setCurrent(page)

    useEffect(() => {
        dispatch(changeHeader(UsersManagement))
    }, [UsersManagement])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const res = await handleGet(`${BASE_URL}get/all/users?search=${searchUser}`)
                setData(res?.users || [])
                setCurrent(1)
            } catch (error) {
                console.log(error)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [searchUser])

    const getInitials = (name = '') =>
        name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

    const avatarColors = [
        'bg-violet-500', 'bg-blue-500', 'bg-emerald-500',
        'bg-rose-500', 'bg-amber-500', 'bg-cyan-500',
    ]
    const getColor = (id) => avatarColors[id % avatarColors.length]

    return (
        <div className="p-4 sm:p-6 lg:p-12 rounded min-h-screen bg-[#020617] shadow">

            {/* Header row */}
            <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-200">All Users</h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{data.length} total users</p>
                </div>
                <Input
                    onChange={e => setSearchUser(e.target.value)}
                    value={searchUser}
                    className="w-full sm:max-w-xs h-10 rounded-xl border border-gray-200 shadow-sm"
                    placeholder="Search users…"
                    prefix={<BiSearch size={18} className="text-gray-400" />}
                    allowClear
                />
            </div>

            {/* Table card — single layout, scrollable on mobile */}
            <div className="bg-[#0b1629] rounded-2xl shadow-sm border border-gray-800 overflow-hidden">

                {/* Horizontally scrollable wrapper */}
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px]">

                        {/* Head */}
                        <thead>
                            <tr className="border-b border-gray-800 bg-[#0b1629]">
                                <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-400 px-4 sm:px-6 lg:px-10 py-3 w-10">#</th>
                                <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 py-3">Name</th>
                                <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 py-3">Email</th>
                                <th className="text-right text-xs font-semibold uppercase tracking-wider text-gray-400 px-4 sm:px-6 lg:px-10 py-3 w-20">Action</th>
                            </tr>
                        </thead>

                        {/* Body */}
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4}>
                                        <div className="flex justify-center items-center py-20">
                                            <Spin size="large" />
                                        </div>
                                    </td>
                                </tr>
                            ) : itemsToDisplay?.length > 0 ? (
                                itemsToDisplay.map(({ name, email, id }, idx) => (
                                    <tr
                                        key={id}
                                        className="border-b border-gray-800 last:border-b-0 hover:bg-[#0f1e38] transition-colors"
                                    >
                                        {/* # */}
                                        <td className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 text-sm text-gray-400 font-medium w-10">
                                            {startIndex + idx + 1}
                                        </td>

                                        {/* Avatar + Name */}
                                        <td className="px-3 py-3 sm:py-4">
                                            <div className="flex items-center gap-2 sm:gap-3">
                                                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold shrink-0 ${getColor(id)}`}>
                                                    {getInitials(name)}
                                                </div>
                                                <span className="text-xs sm:text-sm font-medium text-gray-300 truncate max-w-[90px] sm:max-w-[160px] lg:max-w-none">
                                                    {name}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Email */}
                                        <td className="px-3 py-3 sm:py-4">
                                            <span className="text-xs sm:text-sm text-gray-400 truncate block max-w-[130px] sm:max-w-[220px] lg:max-w-none">
                                                {email}
                                            </span>
                                        </td>

                                        {/* Action */}
                                        <td className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 text-right w-20">
                                            <Link to={`${id}`}>
                                                <button className="inline-flex items-center justify-center text-gray-400 border border-gray-600 hover:border-gray-400 hover:text-gray-200 p-2 sm:px-3 sm:py-1.5 rounded-lg transition-colors">
                                                    <FaEye size={13} />
                                                </button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4}>
                                        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                                            <BiSearch size={40} className="mb-3 opacity-30" />
                                            <p className="text-sm">No users found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer with pagination */}
                {data?.length > itemsPerPage && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 sm:px-6 lg:px-10 py-4 border-t border-gray-800 bg-[#020617]">
                        <span className="text-xs text-gray-400 order-2 sm:order-1">
                            Showing {startIndex + 1}–{Math.min(endIndex, data.length)} of {data.length}
                        </span>
                        <div className="order-1 sm:order-2">
                            <Pagination
                                pageSize={itemsPerPage}
                                current={current}
                                onChange={onChange}
                                total={data?.length}
                                showSizeChanger={false}
                                size="small"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default UserManagement